import { BarChart2, Clock, Database, DollarSign, Link2, RefreshCw, SlidersHorizontal } from "lucide-react";
import React from "react";
import { useTheme } from "../../../../contexts/ThemeContext";
import {
  Contract,
  ContractColumnDef,
  LivePricesResponse,
  SPLIT_RATIO_COLUMN_KEY,
} from "../../../../types/contract";
import { getSplitRatio } from "../../../../utils/contractReferenceData";
import {
  formatCompactCurrency,
  formatMarketCap,
  formatNumber,
  formatVolume,
  safeParseFloat,
} from "../../../../utils/numberUtils";

// Frozen columns config: columns that stay fixed during horizontal scroll
const FROZEN_COLUMN_KEYS = ["conId", "symbol", "longName", "secType", "exchange", "primaryExchange"];
const PRICE_SOURCE_OPTIONS = ["", "EODHD", "IBKR"] as const;
const FROZEN_WIDTHS: Record<string, number> = {
  checkbox: 40,
  conId: 70,
  symbol: 80,
  longName: 200,
  secType: 60,
  currency: 60,
  exchange: 70,
  primaryExchange: 100
};

interface ContractsTableProps {
  contracts: Contract[];
  columns: ContractColumnDef[];
  loading: boolean;
  selectedConIds: Set<number>;
  sortColumn: string;
  sortDirection: "asc" | "desc";
  onSort: (column: string) => void;
  onSelectAll: (checked: boolean) => void;
  onSelectOne: (conId: number, checked: boolean) => void;
  
  // Inline editing props
  editingCell: {conId: number; key: keyof Contract; value: string} | null;
  editValue: string;
  pendingChanges: Map<number, Record<string, {oldValue: string; newValue: string}>>;
  onCellDoubleClick: (contract: Contract, key: keyof Contract) => void;
  onCellChange: (value: string) => void;
  onCellBlur: () => void;
  onCellKeyDown: (e: React.KeyboardEvent) => void;
  
  // Live Price props
  livePrices: LivePricesResponse;
  fetchingPrices: Set<number>;
  onFetchPrice: (conId: number) => void;
  onOpenTradableTimes: (contract: Contract) => void;
  onOpenLivePrice: (contract: Contract) => void;
  onOpenFactors?: (contract: Contract) => void;
  onOpenOverrides?: (contract: Contract) => void;
  onOpenUsage?: (contract: Contract) => void;
  onOpenReferenceData: (contract: Contract) => void;
}

const ContractsTable: React.FC<ContractsTableProps> = ({
  contracts,
  columns,
  loading,
  selectedConIds,
  sortColumn,
  sortDirection,
  onSort,
  onSelectAll,
  onSelectOne,
  editingCell,
  editValue,
  pendingChanges,
  onCellDoubleClick,
  onCellChange,
  onCellBlur,
  onCellKeyDown,
  livePrices,
  fetchingPrices,
  onFetchPrice,
  onOpenTradableTimes,
  onOpenLivePrice,
  onOpenFactors,
  onOpenOverrides,
  onOpenUsage,
  onOpenReferenceData,
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const allSelected = contracts.length > 0 && contracts.every((c) => selectedConIds.has(c.conId));

  // Calculate cumulative left offsets for sticky positioning
  const getStickyStyle = (colKey: string, isHeader: boolean) => {
    let isFrozen = false;
    let width = 0;
    
    if (colKey === "checkbox") {
      isFrozen = true;
      width = FROZEN_WIDTHS.checkbox;
    } else if (FROZEN_COLUMN_KEYS.includes(colKey)) {
      isFrozen = true;
      width = FROZEN_WIDTHS[colKey] || 100;
    }
    
    if (!isFrozen) return {};

    // Calculate left offset
    let left = 0;
    if (colKey !== "checkbox") {
      left += FROZEN_WIDTHS.checkbox;
      for (const key of FROZEN_COLUMN_KEYS) {
        if (key === colKey) break;
        left += FROZEN_WIDTHS[key] || 100;
      }
    }

    return {
      position: "sticky" as const,
      left: `${left}px`,
      width: `${width}px`,
      minWidth: `${width}px`,
      maxWidth: `${width}px`,
      zIndex: isHeader ? 20 : 5,
      backgroundColor: isDarkMode ? (isHeader ? "#0A0A0A" : "#111111") : (isHeader ? "#f9fafb" : "#ffffff"),
      borderRight: colKey === "primaryExchange" ? (isDarkMode ? "2px solid #333333" : "2px solid #d1d5db") : undefined,
      borderBottom: isDarkMode ? "1px solid #333333" : "1px solid #e5e7eb"
    };
  };

  const sortIndicator = (column: string) => {
    if (sortColumn !== column) return null;
    return sortDirection === "asc" ? " ▲" : " ▼";
  };

  const getPriceDisplay = (conId: number, source: "IBKR" | "YAHOO" | "EODHD") => {
    const priceData = livePrices[String(conId)];
    if (!priceData) return null;
    const info = priceData.find((p) => p.source === source);
    if (!info) return null;
    // Show raw numeric value as-is (deliberate behavior change -- no "Error" fallback)
    return { value: formatNumber(info.price), error: false };
  };

  const emptyCell = <span className={isDarkMode ? "text-rp-grey/40" : "text-gray-400"}>—</span>;

  const formatCellValue = (contract: Contract, column: ContractColumnDef) => {
    // Split ratio has no contract column -- it lives only in the referenceData JSON and is
    // never inline-editable, so it never has a pending change.
    if (column.key === SPLIT_RATIO_COLUMN_KEY) {
      return getSplitRatio(contract) ?? emptyCell;
    }

    const key = column.key;
    const pending = pendingChanges.get(contract.conId)?.[key];
    const value = pending ? (pending.newValue || null) : contract[key];

    if (value === null || value === undefined) {
      return emptyCell;
    }

    switch (key) {
      case "optionAllowed":
      case "shortable":
        return String(value);

      case "verifiedAtUtc":
      case "lastUsedAtUtc":
      case "createdAt":
      case "updatedAt":
      case "latestSplitDt":
        return new Date(value as string).toLocaleDateString();

      case "marketCap":
      case "marketCapUsd":
        return formatMarketCap(value as number | string);

      case "volume":
        return formatVolume(value as number | string);

      case "avgDollarVolume":
        return formatCompactCurrency(value as number | string);

      case "shortableValue":
        return formatNumber(safeParseFloat(value as number | string));

      case "shortableShares":
        return formatVolume(value as number | string);

      case "validExchanges": {
        const str = String(value);
        return str.length > 12 ? <span title={str}>{str.substring(0, 12)}...</span> : str;
      }

      default:
        return String(value);
    }
  };

  return (
    <div className={`flex-1 overflow-auto border rounded-lg ${isDarkMode ? "border-rp-separator" : "border-gray-200"}`}>
      <table className={`min-w-full divide-y ${isDarkMode ? "divide-rp-separator" : "divide-gray-200"}`}>
        <thead className={`${isDarkMode ? "bg-rp-surface-sunken" : "bg-gray-50"} sticky top-0 z-10`}>
          <tr>
            <th className="px-4 py-3 w-10" style={getStickyStyle("checkbox", true)}>
              <input
                type="checkbox"
                checked={allSelected}
                onChange={(e) => onSelectAll(e.target.checked)}
                className="rounded border-gray-300"
              />
            </th>
            {columns.map((col) => {
              const isFrozen = FROZEN_COLUMN_KEYS.includes(col.key);
              return (
                <th
                  key={col.key}
                  className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider whitespace-nowrap ${
                    isDarkMode ? "text-rp-grey/70" : "text-gray-500"
                  } ${col.sortable ? "cursor-pointer select-none hover:bg-gray-600/20" : ""}`}
                  style={isFrozen ? getStickyStyle(col.key, true) : undefined}
                  onClick={() => col.sortable && onSort(col.key)}
                >
                  {col.label}
                  {col.sortable && sortIndicator(col.key)}
                </th>
              );
            })}
            
            {/* Price Columns */}
            <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider whitespace-nowrap ${isDarkMode ? "text-rp-grey/70" : "text-gray-500"}`}>
              Prices
            </th>
            <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider whitespace-nowrap ${isDarkMode ? "text-rp-grey/70" : "text-gray-500"}`}>
              IBKR
            </th>
            <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider whitespace-nowrap ${isDarkMode ? "text-rp-grey/70" : "text-gray-500"}`}>
              Yahoo
            </th>
            <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider whitespace-nowrap ${isDarkMode ? "text-rp-grey/70" : "text-gray-500"}`}>
              EODHD
            </th>
             <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider whitespace-nowrap ${isDarkMode ? "text-rp-grey/70" : "text-gray-500"}`}>
              Hours
            </th>
            {onOpenFactors && (
              <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider whitespace-nowrap ${isDarkMode ? "text-rp-grey/70" : "text-gray-500"}`}>
                Factors
              </th>
            )}
            {onOpenOverrides && (
              <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider whitespace-nowrap ${isDarkMode ? "text-rp-grey/70" : "text-gray-500"}`}>
                Overrides
              </th>
            )}
            <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider whitespace-nowrap ${isDarkMode ? "text-rp-grey/70" : "text-gray-500"}`}>
              Usage
            </th>
            <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider whitespace-nowrap ${isDarkMode ? "text-rp-grey/70" : "text-gray-500"}`}>
              Ref Data
            </th>
          </tr>
        </thead>
        <tbody className={`divide-y ${isDarkMode ? "divide-rp-separator bg-rp-surface" : "divide-gray-200 bg-white"}`}>
          {contracts.map((contract) => (
            <tr key={contract.conId} className={isDarkMode ? "hover:bg-rp-surface-raised" : "hover:bg-gray-50"}>
              <td className="px-4 py-3" style={getStickyStyle("checkbox", false)}>
                <input
                  type="checkbox"
                  checked={selectedConIds.has(contract.conId)}
                  onChange={(e) => onSelectOne(contract.conId, e.target.checked)}
                  className="rounded border-gray-300"
                />
              </td>
              {columns.map((col) => {
                const isFrozen = FROZEN_COLUMN_KEYS.includes(col.key);
                const isEditing = editingCell?.conId === contract.conId && editingCell?.key === col.key;
                const hasPendingChange = pendingChanges.get(contract.conId)?.[col.key];
                // Synthetic columns have no contract field to edit. Reference-data values
                // (marketCap, marketCapUsd, volume, latestSplitDt, avgDollarVolume) are read-only
                // on the upsert path and are corrected through the reference-data modal instead.
                // Remarks are shown here but managed on the contract remarks page.
                const editableKey = col.key === SPLIT_RATIO_COLUMN_KEY ? null : col.key;
                const isEditable = editableKey !== null && editableKey !== "id" && editableKey !== "conId" && editableKey !== "createdAt" && editableKey !== "updatedAt" && editableKey !== "lastUsedAtUtc" && editableKey !== "remarks" && editableKey !== "marketCap" && editableKey !== "marketCapUsd" && editableKey !== "volume" && editableKey !== "avgDollarVolume" && editableKey !== "latestSplitDt" && editableKey !== "shortableValue" && editableKey !== "shortableShares";

                const stickyStyle = isFrozen ? getStickyStyle(col.key, false) : undefined;
                const bgOverride = hasPendingChange 
                  ? (isDarkMode ? "rgb(55, 45, 25)" : "rgb(254, 252, 232)")
                  : (selectedConIds.has(contract.conId) 
                      ? (isDarkMode ? "rgb(30, 58, 95)" : "rgb(239, 246, 255)") 
                      : undefined);
                
                const finalStyle = stickyStyle ? { ...stickyStyle, backgroundColor: bgOverride || stickyStyle.backgroundColor } : (bgOverride ? { backgroundColor: bgOverride } : undefined);

                return (
                  <td
                    key={col.key}
                    className={`px-4 py-3 text-sm whitespace-nowrap ${isDarkMode ? "text-rp-grey/70" : "text-gray-900"} ${isEditable ? "cursor-text" : ""}`}
                    style={finalStyle}
                    onDoubleClick={() => isEditable && editableKey && onCellDoubleClick(contract, editableKey)}
                  >
                    {isEditing ? (
                      col.key === "priceSource" ? (
                        <select
                          autoFocus
                          value={editValue}
                          onChange={(e) => {
                            onCellChange(e.target.value);
                          }}
                          onBlur={onCellBlur}
                          onKeyDown={onCellKeyDown}
                          className={`w-full px-2 py-1 rounded border text-sm focus:outline-none focus:ring-1 ${
                            isDarkMode
                              ? "bg-rp-surface border-rp-separator text-rp-grey focus:border-rp-amber focus:ring-rp-amber"
                              : "bg-white border-gray-300 text-black focus:border-blue-500 focus:ring-blue-500"
                          }`}
                        >
                          {PRICE_SOURCE_OPTIONS.map((opt) => (
                            <option key={opt} value={opt}>{opt || "None"}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          autoFocus
                          type="text"
                          value={editValue}
                          onChange={(e) => onCellChange(e.target.value)}
                          onBlur={onCellBlur}
                          onKeyDown={onCellKeyDown}
                          className={`w-full px-2 py-1 rounded border text-sm focus:outline-none focus:ring-1 ${
                            isDarkMode
                              ? "bg-rp-surface border-rp-separator text-rp-grey focus:border-rp-amber focus:ring-rp-amber"
                              : "bg-white border-gray-300 text-black focus:border-blue-500 focus:ring-blue-500"
                          }`}
                        />
                      )
                    ) : (
                      formatCellValue(contract, col)
                    )}
                  </td>
                );
              })}
              
              {/* Price Action Column */}
              <td className="px-4 py-3 text-sm whitespace-nowrap border-l border-gray-200 dark:border-rp-separator">
                 <button
                    onClick={() => onFetchPrice(contract.conId)}
                    disabled={fetchingPrices.has(contract.conId)}
                    className={`p-1 rounded hover:bg-gray-100 dark:hover:bg-rp-surface-raised ${fetchingPrices.has(contract.conId) ? "animate-spin text-blue-500" : "text-gray-500"}`}
                    title="Fetch Live Price"
                  >
                    <RefreshCw className="w-3 h-3" />
                  </button>
                   <button
                    onClick={() => onOpenLivePrice(contract)}
                    className="p-1 rounded hover:bg-gray-100 dark:hover:bg-rp-surface-raised text-gray-500 ml-1"
                    title="Price Details"
                  >
                    <DollarSign className="w-3 h-3" />
                  </button>
              </td>

              {/* Price Columns Data */}
              {["IBKR", "YAHOO", "EODHD"].map((source) => {
                 const price = getPriceDisplay(contract.conId, source as any);
                 return (
                   <td key={source} className="px-4 py-3 text-sm whitespace-nowrap text-right font-mono">
                     {price ? (
                        <span className={price.error ? "text-red-500" : (isDarkMode ? "text-green-400" : "text-green-600")}>
                          {price.value}
                        </span>
                     ) : <span className="text-gray-400">-</span>}
                   </td>
                 );
              })}

              {/* Tradable Times Action */}
              <td className="px-4 py-3 text-sm whitespace-nowrap text-center">
                 <button
                    onClick={() => onOpenTradableTimes(contract)}
                    className="p-1 rounded hover:bg-gray-100 dark:hover:bg-rp-surface-raised text-gray-500"
                    title="View Tradable Hours"
                  >
                    <Clock className="w-3 h-3" />
                  </button>
              </td>
              {onOpenFactors && (
                <td className="px-4 py-3 text-sm whitespace-nowrap text-center">
                  <button
                    onClick={() => onOpenFactors(contract)}
                    className="px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium inline-flex items-center gap-1.5 transition-colors"
                    title="View Factors"
                  >
                    <BarChart2 className="w-4 h-4" />
                    Factors
                  </button>
                </td>
              )}
              {onOpenOverrides && (
                <td className="px-4 py-3 text-sm whitespace-nowrap text-center">
                  <button
                    onClick={() => onOpenOverrides(contract)}
                    className="px-3 py-1.5 rounded-md bg-purple-600 hover:bg-purple-700 text-white text-xs font-medium inline-flex items-center gap-1.5 transition-colors"
                    title="Manage Price Overrides"
                  >
                    <SlidersHorizontal className="w-4 h-4" />
                    Overrides
                  </button>
                </td>
              )}
              <td className="px-4 py-3 text-sm whitespace-nowrap text-center">
                <button
                  onClick={() => onOpenUsage?.(contract)}
                  className="px-3 py-1.5 rounded-md bg-gray-600 hover:bg-gray-700 text-white text-xs font-medium inline-flex items-center gap-1.5 transition-colors"
                  title="Show Usage"
                >
                  <Link2 className="w-4 h-4" />
                  Usage
                </button>
              </td>
              <td className="px-4 py-3 text-sm whitespace-nowrap text-center">
                <button
                  onClick={() => onOpenReferenceData(contract)}
                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-rp-surface-raised text-gray-500"
                  title="Refresh Reference Data"
                  aria-label={`Refresh reference data for ${contract.symbol}`}
                >
                  <Database className="w-3 h-3" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ContractsTable;
